import { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "@/lib/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "mediastore_chatbot_session";

const WELCOME_MESSAGE = Object.freeze({
  id: "welcome",
  role: "assistant",
  content:
    "Bonjour ! Je suis l'assistant Mediastore Niort. Posez-moi vos questions sur la réparation, les tarifs, ou prenez rendez-vous.",
});

const newMessageId = () =>
  `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Conversation state + API integration. Centralizes message handling so the
 * UI component stays presentational.
 */
export function useChatbot() {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);

  // Restore session id from localStorage once on mount.
  // localStorage stores ONLY a non-sensitive conversation UUID (no auth, no PII).
  useEffect(() => {
    try {
      const stored = window.localStorage?.getItem(STORAGE_KEY);
      if (stored) sessionRef.current = stored;
    } catch (err) {
      // localStorage unavailable (SSR, Safari private mode, disabled storage)
      logger.debug("[Chatbot] localStorage read unavailable:", err?.message || err);
    }
  }, []);

  const send = useCallback(async (question) => {
    const trimmed = (question || "").trim();
    if (!trimmed) return;

    const userMsg = { id: newMessageId(), role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const recentHistory = (msgs) =>
        msgs
          .filter((m) => m.id !== WELCOME_MESSAGE.id)
          .slice(-6)
          .map((m) => ({ role: m.role, content: m.content }));

      let history = [];
      setMessages((prev) => {
        history = recentHistory(prev);
        return prev;
      });

      const res = await fetch(`${API_URL}/api/chatbot/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: trimmed,
          session_id: sessionRef.current,
          history,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.session_id && data.session_id !== sessionRef.current) {
        sessionRef.current = data.session_id;
        try {
          window.localStorage?.setItem(STORAGE_KEY, data.session_id);
        } catch (err) {
          // localStorage write failed — keep session in-memory only
          logger.debug("[Chatbot] localStorage write unavailable:", err?.message || err);
        }
      }
      setMessages((prev) => [
        ...prev,
        {
          id: newMessageId(),
          role: "assistant",
          content: data.answer || "Désolé, je n'ai pas compris.",
        },
      ]);
    } catch (err) {
      logger.error("[Chatbot] send() failed:", err?.message || err);
      setMessages((prev) => [
        ...prev,
        {
          id: newMessageId(),
          role: "assistant",
          content:
            "Désolé, une erreur est survenue. Vous pouvez nous appeler directement au 05 49 05 74 90.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { messages, loading, send };
}
